import { isMoment } from 'moment'
import { isFinite } from 'lodash'

import { FULL_DATE_WITHOUT_TIMEZONE } from '../constants'
import { areMutuallyExcluded, getIdOrNull, isObject, isPositive } from '..'
import { API_DATE, getTimezoneLessMoment } from '../momentjs'
import { Properties } from '../entities'

/** @enum {string} */
export const QuotaProperties = Object.freeze({
  SECTOR: Properties.SECTOR,
  SECTION: Properties.SECTION,
  SECTION_GROUP: Properties.SECTION_GROUP,
  SCHOOL: Properties.SCHOOL,
  SECTION_TO_YEAR: Properties.SECTION_TO_YEAR,
  START_DATE: Properties.START_DATE,
  END_DATE: Properties.END_DATE,
  TOTAL: Properties.TOTAL
})

export const QuotaValidators = Object.freeze({
  [QuotaProperties.SECTION]: quota => areMutuallyExcluded(quota[QuotaProperties.SECTION], quota[QuotaProperties.SECTION_GROUP]),
  [QuotaProperties.SECTION_GROUP]: quota => areMutuallyExcluded(quota[QuotaProperties.SECTION_GROUP], quota[QuotaProperties.SECTION]),
  [QuotaProperties.SECTOR]: quota => isFinite(getIdOrNull(quota.sector)),
  [QuotaProperties.SCHOOL]: quota => !!getIdOrNull(quota.school),
  [QuotaProperties.START_DATE]: quota => isMoment(quota.startDate),
  [QuotaProperties.END_DATE]: quota => isMoment(quota.endDate),
  [QuotaProperties.TOTAL]: quota => isPositive(quota.total)
})

export const areQuotaEquivalent = (first, second) => {
  if (first == null || second == null) {
    return false
  }

  const firstQuota = first.quota ?? first
  const secondQuota = second.quota ?? second

  const withIdKeys = [QuotaProperties.SECTOR, QuotaProperties.SCHOOL, QuotaProperties.SECTION, QuotaProperties.SECTION_TO_YEAR]
  const datesKeys = [QuotaProperties.START_DATE, QuotaProperties.END_DATE]

  const unequivalentKeys = Object.keys(firstQuota).filter(key => {
    if (datesKeys.includes(key)) {
      return getTimezoneLessMoment(firstQuota[key]).format(API_DATE) !== getTimezoneLessMoment(secondQuota[key])?.format(API_DATE)
    } else if (withIdKeys.includes(key)) {
      return getIdOrNull(firstQuota?.[key]) !== getIdOrNull(secondQuota?.[key])
    } else {
      return firstQuota[key] !== secondQuota[key]
    }
  })

  return unequivalentKeys.length === 0
}

export const quotaGetters = {
  sectorName: q => isObject(q.sector) ? q.sector.name : null,
  sectionName: q => isObject(q.section) ? q.section.abbreviation : null,
  sectionGroupName: q => isObject(q.sectionGroup) ? q.sectionGroup.name : null,
  schoolName: q => isObject(q.school) ? q.school.abbreviation ?? q.school.name : null,
  yearName: q => isObject(q.sectionToYear) ? q.sectionToYear.schoolYearAbbreviation ?? q.sectionToYear.schoolYearName : null,
  total: q => q.total
}

export class Quota {
  constructor ({
    id = -1, school = null, sector = null, section = null, sectionGroup = null, sectionToYear = null, total = 1, startDate = null, endDate = null, isDesiderata = false
  } = {}) {
    this.id = id
    this.startDate = getTimezoneLessMoment(startDate)
    this.endDate = getTimezoneLessMoment(endDate)
    this.school = school
    this.sector = sector
    this.section = section
    this.sectionGroup = sectionGroup
    this.sectionToYear = sectionToYear
    this.total = total
    this.isDesiderata = isDesiderata
  }

  toRequestBody (useId = false) {
    const body = {
      sector: getIdOrNull(this.sector),
      section: getIdOrNull(this.section),
      sectionGroup: getIdOrNull(this.sectionGroup),
      school: getIdOrNull(this.school),
      sectionToYear: getIdOrNull(this.sectionToYear),
      startDate: getTimezoneLessMoment(this.startDate).format(FULL_DATE_WITHOUT_TIMEZONE),
      endDate: getTimezoneLessMoment(this.endDate).format(FULL_DATE_WITHOUT_TIMEZONE),
      total: getIdOrNull(this.total),
      isDesiderata: this.isDesiderata
    }

    if (useId) {
      body.id = this.id
    }

    return body
  }
}
